import { useContent } from '@/content';
import { Editable } from '@/content/design';

export default function TreatmentsOverview() {
  const c = useContent();
  return (
    <section id="behandlungen" className="py-20 md:py-28 bg-warm-light relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-blush-50 blur-3xl -translate-y-1/2 -translate-x-1/2"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-champagne-50 blur-3xl translate-y-1/2 translate-x-1/2"></div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16 md:mb-20 animate-on-scroll">
          <Editable as="p" id="treatments.eyebrow" label="Behandlungen – Kleine Überschrift" className="text-xs tracking-[0.3em] uppercase text-champagne-500 font-medium mb-3">
            {c.treatments.eyebrow}
          </Editable>
          <Editable as="h2" id="treatments.title" label="Behandlungen – Titel" className="font-display text-3xl md:text-4xl lg:text-5xl text-gray-800 mb-4">
            {c.treatments.title}
          </Editable>
          <Editable as="p" id="treatments.subtitle" label="Behandlungen – Untertitel" className="text-gray-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            {c.treatments.subtitle}
          </Editable>
        </div>

        {/* Block 1: Gesichtsbehandlungen - Image left, text right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-20 md:mb-28">
          <div className="animate-on-scroll-left">
            <div className="relative rounded-2xl overflow-hidden shadow-xl h-[320px] md:h-[420px] lg:h-[480px]">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20facial%20beauty%20device%20and%20skincare%20equipment%20on%20elegant%20marble%20table%20in%20luxurious%20spa%2C%20soft%20champagne%20gold%20and%20blush%20pink%20tones%2C%20ultrasonic%20cleanser%20and%20cosmetic%20tools%20arranged%20aesthetically%2C%20warm%20lighting%2C%20clean%20minimalist%20background%2C%20sophisticated%20beauty%20studio%20aesthetic%2C%20high-end%20cosmetic%20instruments&width=800&height=600&seq=101&orientation=landscape"
                alt="Gesichtsbehandlung im Kosmetikstudio Gesichtszauber"
                title="Professionelle Gesichtsbehandlung – Kosmetikstudio Gesichtszauber Wertingen"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
            </div>
          </div>

          <div className="animate-on-scroll-right">
            <Editable as="p" id="treatments.block1.eyebrow" label="Gesichtsbehandlungen – Kleine Überschrift" className="text-xs tracking-[0.3em] uppercase text-champagne-500 font-medium mb-3">
              {c.treatments.block1.eyebrow}
            </Editable>
            <Editable as="h3" id="treatments.block1.title" label="Gesichtsbehandlungen – Titel" className="font-display text-2xl md:text-3xl lg:text-4xl text-gray-800 mb-5 leading-tight">
              {c.treatments.block1.title}
            </Editable>
            <div className="space-y-4 text-gray-600 leading-relaxed text-sm md:text-base">
              <Editable as="p" id="treatments.block1.text" label="Gesichtsbehandlungen – Text">{c.treatments.block1.text}</Editable>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.treatments.block1.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block bg-blush-50 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full border border-blush-100"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Block 2: Spezialbehandlungen - Text left, image right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-20 md:mb-28">
          <div className="order-2 lg:order-1 animate-on-scroll-left">
            <Editable as="p" id="treatments.block2.eyebrow" label="Spezialbehandlungen – Kleine Überschrift" className="text-xs tracking-[0.3em] uppercase text-champagne-500 font-medium mb-3">
              {c.treatments.block2.eyebrow}
            </Editable>
            <Editable as="h3" id="treatments.block2.title" label="Spezialbehandlungen – Titel" className="font-display text-2xl md:text-3xl lg:text-4xl text-gray-800 mb-5 leading-tight">
              {c.treatments.block2.title}
            </Editable>
            <div className="space-y-4 text-gray-600 leading-relaxed text-sm md:text-base">
              <Editable as="p" id="treatments.block2.text1" label="Spezialbehandlungen – Text 1" html={c.treatments.block2.text1} />
              <Editable as="p" id="treatments.block2.text2" label="Spezialbehandlungen – Text 2" html={c.treatments.block2.text2} />
              <Editable as="p" id="treatments.block2.text3" label="Spezialbehandlungen – Text 3" html={c.treatments.block2.text3} />
              <Editable as="p" id="treatments.block2.text4" label="Spezialbehandlungen – Text 4" html={c.treatments.block2.text4} />
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.treatments.block2.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block bg-champagne-50 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full border border-champagne-100"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="order-1 lg:order-2 animate-on-scroll-right">
            <div className="relative rounded-2xl overflow-hidden shadow-xl h-[320px] md:h-[420px] lg:h-[480px]">
              <img
                src="https://readdy.ai/api/search-image?query=Advanced%20microneedling%20and%20mesoporation%20beauty%20devices%20on%20elegant%20surface%2C%20professional%20cosmetic%20technology%20equipment%2C%20champagne%20gold%20and%20blush%20pink%20color%20palette%2C%20modern%20skincare%20instruments%20with%20LED%20display%2C%20warm%20soft%20lighting%2C%20clean%20minimalist%20spa%20background%2C%20luxurious%20aesthetic%2C%20high-tech%20beauty%20tools&width=800&height=600&seq=102&orientation=landscape"
                alt="Spezialbehandlung Microneedling im Studio"
                title="Microneedling und Mesoporation Spezialbehandlung – Gesichtszauber Wertingen"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Block 3: Schöne Augen - Image left, text right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="animate-on-scroll-left">
            <div className="relative rounded-2xl overflow-hidden shadow-xl h-[320px] md:h-[420px] lg:h-[480px]">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20eyebrow%20and%20eyelash%20beauty%20tools%20on%20elegant%20marble%20tray%2C%20precision%20tweezers%20brushes%20and%20tinting%20applicators%2C%20soft%20champagne%20gold%20and%20blush%20pink%20tones%2C%20luxurious%20spa%20setting%2C%20warm%20lighting%2C%20clean%20minimalist%20background%2C%20sophisticated%20eye%20beauty%20treatment%20aesthetic%2C%20cosmetic%20instruments%20arranged%20beautifully&width=800&height=600&seq=103&orientation=landscape"
                alt="Augenbrauen und Wimpern Behandlung"
                title="Augenbrauen und Wimpern Behandlung – Kosmetikstudio Gesichtszauber Wertingen"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
            </div>
          </div>

          <div className="animate-on-scroll-right">
            <Editable as="p" id="treatments.block3.eyebrow" label="Schöne Augen – Kleine Überschrift" className="text-xs tracking-[0.3em] uppercase text-champagne-500 font-medium mb-3">
              {c.treatments.block3.eyebrow}
            </Editable>
            <Editable as="h3" id="treatments.block3.title" label="Schöne Augen – Titel" className="font-display text-2xl md:text-3xl lg:text-4xl text-gray-800 mb-5 leading-tight">
              {c.treatments.block3.title}
            </Editable>
            <div className="space-y-4 text-gray-600 leading-relaxed text-sm md:text-base">
              <Editable as="p" id="treatments.block3.text1" label="Schöne Augen – Text 1">{c.treatments.block3.text1}</Editable>
              <Editable as="p" id="treatments.block3.text2" label="Schöne Augen – Text 2">{c.treatments.block3.text2}</Editable>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.treatments.block3.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block bg-blush-50 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full border border-blush-100"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}